import React from 'react';
import {
  AppBar, Toolbar, IconButton, Avatar, Menu, MenuItem,
  Typography, Box, Divider,
} from '@mui/material';
import {
  Person, Brightness4, Brightness7,
  Logout, Settings, Menu as MenuIcon, ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User } from '@/models/user';
import './UnifiedHeader.css';

interface UnifiedHeaderProps {
  user?: User;
  onLogout?: () => void;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  onSidebarToggle?: () => void;
  pageTitle?: string;
  pageSubtitle?: string;
  showBreadcrumbs?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  user, onLogout, darkMode, setDarkMode, onSidebarToggle, pageTitle, showBackButton, onBack,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { handleMenuClose(); onLogout?.(); };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        className={`unified-header ${darkMode ? 'dark' : 'light'}`}
      >
        <Toolbar className="header-toolbar" sx={{ width: '100%', minHeight: '56px !important', px: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {/* Sidebar toggle */}
            {onSidebarToggle && (
              <IconButton
                onClick={onSidebarToggle}
                size="small"
                className="header-icon-btn"
                sx={{ mr: 1 }}
                aria-label="Toggle navigation"
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            )}

            {/* Back button */}
            {showBackButton && (
              <IconButton
                onClick={onBack}
                size="small"
                className="header-icon-btn"
                sx={{ mr: 1 }}
                aria-label="Go back"
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            )}

            {/* Page title */}
            <Typography variant="subtitle1" className="header-page-title" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {pageTitle || ''}
            </Typography>
          </Box>

          {/* Right actions */}
          <Box className="header-actions" sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexShrink: 0 }}>
            <IconButton
              onClick={() => setDarkMode(!darkMode)}
              size="small"
              className="header-icon-btn"
              title={darkMode ? 'Light mode' : 'Dark mode'}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
            </IconButton>

            {user && (
              <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 0.5 }} aria-label="User menu">
                <Avatar className="user-avatar" sx={{ width: 30, height: 30, fontSize: '0.8rem' }}>
                  {user.username?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {user && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{ className: `user-menu-paper ${darkMode ? 'dark' : 'light'}`, elevation: 2 }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.username || user?.email || 'Current User'}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
            <Settings sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2">Settings</Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <Logout sx={{ mr: 1.5, fontSize: 18 }} />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default UnifiedHeader;
